import { Tag } from '@chakra-ui/react';
import FormatAsCurrency from 'common/utils/formatAsCurrency';
import IconImage from 'common/components/icon-image';
import Button from 'common/components/button';

const PaymentLine = ({ title, amount, currency, isTotal = false }) => (
  <div
    className={`grid grid-cols-[1fr_auto] gap-x-4 ${
      isTotal ? 'font-medium text-base border-t border-gray-200 pt-2 mt-1' : ''
    }`}
  >
    <span className={`${isTotal ? '' : 'text-gray-500'}`}>{title}</span>
    <span className={`text-right ${isTotal ? 'font-medium' : ''}`}>
      {FormatAsCurrency(amount, currency)}
    </span>
  </div>
);

const ClaimPaymentSummary = ({ claim, onClick, isLoading = false, isComplete = false, showSavings = true }) => {
  const { memberPayment, planId } = claim || {};
  const { deductible, nonEligibleCharges, remainingFees } = memberPayment || {};
  const currency = planId?.lease?.currency || 'USD';

  const totalPayment =
    (deductible || 0) + (nonEligibleCharges || 0) + (remainingFees || 0);

  const paymentItems = [
    {
      condition: deductible > 0,
      title: 'Deductible',
      amount: deductible,
    },
    {
      condition: nonEligibleCharges > 0,
      title: 'Non-Eligible Charges',
      amount: nonEligibleCharges,
    },
    {
      condition: remainingFees > 0,
      title: 'Remaining ReLease Fees',
      amount: remainingFees,
    },
  ].filter((item) => item.condition);

  if (totalPayment <= 0) {
    return null;
  }

  const lease = planId?.lease;
  const property = planId?.property;

  let leaseBreakCost = 2 * (lease?.monthlyRent || 0);
  if (property?.propertyDefaults?.terminationPolicy?.buyoutMonths) {
    leaseBreakCost =
      property.propertyDefaults.terminationPolicy.buyoutMonths *
      (lease?.monthlyRent || 0);
  }

  // The total cost with ReLease for the claim is totalPayment.
  // The savings is the difference between breaking the lease without ReLease and with ReLease.
  const savings = leaseBreakCost > 0 ? leaseBreakCost - totalPayment : 0;

  return (
    <div className='flex flex-col gap-4 col-span-2'>
      <div className='flex flex-col gap-1'>
        <h2 className='capitalize text-lg font-medium'>Payment Summary</h2>
      </div>
      <div className='flex flex-col gap-2 text-base'>
        {paymentItems.map((item) => (
          <PaymentLine
            key={item.title}
            title={item.title}
            amount={item.amount}
            currency={currency}
          />
        ))}
        {paymentItems.length > 1 && (
          <PaymentLine
            title='Total Payment'
            amount={totalPayment}
            currency={currency}
            isTotal={true}
          />
        )}
      </div>
      {savings > 0 && showSavings && (
        <Tag
          colorScheme='green'
          size='sm'
          className='w-full px-3 py-1.5 flex gap-2 mt-1'
        >
          <div className='flex gap-x-1 w-full font-medium items-center'>
            <span className='inline-flex gap-2 items-center'>
              <IconImage size='3xs' className='-mt-px' dark />
              {
                isComplete ? 'You\'ve saved' : 'You\'re saving'
              }
            </span>
            <span className='font-medium text-green-800'>
              {FormatAsCurrency(savings, currency)}
            </span>
            <span>
              on your early move-out with ReLease
            </span>
          </div>
        </Tag>
      )}

      {onClick && (
        <Button
          className='w-full mt-3'
          theme='primary'
          title='Complete Payment'
          onClick={onClick}
          loading={isLoading}
          disabled={isLoading}
        />
      )}

      {!isComplete && onClick && (
        <div className='flex flex-col gap-3 text-sm text-gray-500 mt-3'>
          <div>
            <span className='font-semibold'>
              By clicking Complete Payment, you give permission to ReLease to charge your provided payment method for the amounts above
            </span>
            , and agree to ReLease's{' '}
            <a
              href='https://www.releaserent.com/legal/terms-of-service'
              target='_blank'
              rel='noopener noreferrer'
              className='hover:underline'
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href='https://www.releaserent.com/legal/privacy-policy'
              target='_blank'
              rel='noopener noreferrer'
              className='hover:underline'
            >
              Privacy Policy
            </a>
            .
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimPaymentSummary;